import { z } from "zod";
import { TeamRoles } from "./team-roles.ts";
import { OverlayTemplateContentDto } from "./overlays.ts";
import { AssetMediaType, AssetMediaStatus } from "./asset-media.ts";
import { BillingAccountCustomPricingDto } from "./billing.ts";

// Manual Intercom Sync DTOs
export const ManualIntercomSyncRequestDto = z.object({
  startId: z.number().optional(),
  endId: z.number().optional(),
  limit: z.number().min(1).max(100).default(50).optional(),
});

export const ManualIntercomSyncResponseDto = z.object({
  totalUsers: z.number(),
  successfulUsers: z.number(),
  failedUsers: z.number(),
  errors: z.array(z.string()),
  lastProcessedId: z.number().optional(),
});

// User-related DTOs
export const UserRangeInfoDto = z.object({
  minUserId: z.number(),
  maxUserId: z.number(),
  totalUsers: z.number(),
});

export const AdminUserDto = z.object({
  id: z.number(),
  externalId: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  contactEmail: z.string(),
  createdAt: z.string(),
});

export const UserSearchQueryDto = z.object({
  email: z.string(),
});

export const UserIdParamsDto = z.object({
  userId: z.string(),
});

// Billing Account DTOs
export const BillingAccountDto = z.object({
  billingAccountId: z.number(),
  billingAccountExternalId: z.string(),
  creditBalance: z.number(),
  teamId: z.number(),
  teamExternalId: z.string(),
  teamName: z.string().nullable(),
  stripeCustomerId: z.string().nullable(),
});

export const AddCreditsRequestDto = z.object({
  billingAccountId: z.number(),
  credits: z.number().min(1),
  userEmail: z.string(),
});

export const AddCreditsResponseDto = z.object({
  success: z.boolean(),
  newBalance: z.number(),
  creditIncreaseExternalId: z.string(),
});

// User Role DTOs
export const UserRoleDto = z.object({
  id: z.number(),
  role: z.string(),
  createdAt: z.string(),
});

export const AssignRoleRequestDto = z.object({
  role: z.enum(["amplifiles-superuser", "test-role"]),
  userEmail: z.string(),
});

export const AssignRoleResponseDto = z.object({
  success: z.boolean(),
  roleId: z.number(),
  message: z.string(),
});

// Organization DTOs
export const OrganisationDto = z.object({
  id: z.number(),
  externalId: z.string(),
  name: z.string(),
});

// Team DTOs
export const CreateTeamRequestDto = z.object({
  name: z.string().min(1),
  organisationId: z.number().optional(),
});

export const AdminTeamDto = z.object({
  id: z.number(),
  name: z.string(),
  externalId: z.string(),
});

export const TeamIdParamsDto = z.object({
  teamId: z.string(),
});

// Invite DTOs
export const CreateInviteAdminRequestDto = z.object({
  email: z.string(),
  role: z.enum(TeamRoles),
  teamId: z.number(),
});

// Type exports
export type ManualIntercomSyncRequestDto = z.infer<
  typeof ManualIntercomSyncRequestDto
>;
export type ManualIntercomSyncResponseDto = z.infer<
  typeof ManualIntercomSyncResponseDto
>;
export type UserRangeInfoDto = z.infer<typeof UserRangeInfoDto>;
export type AdminUserDto = z.infer<typeof AdminUserDto>;
export type UserSearchQueryDto = z.infer<typeof UserSearchQueryDto>;
export type UserIdParamsDto = z.infer<typeof UserIdParamsDto>;
export type BillingAccountDto = z.infer<typeof BillingAccountDto>;
export type AddCreditsRequestDto = z.infer<typeof AddCreditsRequestDto>;
export type AddCreditsResponseDto = z.infer<typeof AddCreditsResponseDto>;
export type UserRoleDto = z.infer<typeof UserRoleDto>;
export type AssignRoleRequestDto = z.infer<typeof AssignRoleRequestDto>;
export type AssignRoleResponseDto = z.infer<typeof AssignRoleResponseDto>;
export type OrganisationDto = z.infer<typeof OrganisationDto>;
export type CreateTeamRequestDto = z.infer<typeof CreateTeamRequestDto>;
export type AdminTeamDto = z.infer<typeof AdminTeamDto>;
export type TeamIdParamsDto = z.infer<typeof TeamIdParamsDto>;
export type CreateInviteAdminRequestDto = z.infer<
  typeof CreateInviteAdminRequestDto
>;

// Overlay Template DTOs
export const OverlayTemplateIdParamsDto = z.object({
  overlayTemplateId: z.string(),
});

export const AdminOverlayTemplateDto = z.object({
  id: z.number(),
  name: z.string(),
  teamId: z.number(),
});

// Sync Billing DTOs
export const SyncBillingRequestDto = z.object({
  teamId: z.number(),
  userEmail: z.string(),
});

export const SyncBillingResponseDto = z.object({
  success: z.boolean(),
});

export type SyncBillingRequestDto = z.infer<typeof SyncBillingRequestDto>;
export type SyncBillingResponseDto = z.infer<typeof SyncBillingResponseDto>;

// Copy Overlay Template DTOs
export const CopyOverlayTemplateRequestDto = z.object({
  sourceTeamId: z.number(),
  targetTeamId: z.number(),
  overlayTemplateId: z.number(),
});

export const CopyOverlayTemplateResponseDto = z.object({
  success: z.boolean(),
  newTemplateId: z.number(),
  copiedAssetMediaCount: z.number(),
});

export type OverlayTemplateIdParamsDto = z.infer<
  typeof OverlayTemplateIdParamsDto
>;
export type AdminOverlayTemplateDto = z.infer<typeof AdminOverlayTemplateDto>;
export type CopyOverlayTemplateRequestDto = z.infer<
  typeof CopyOverlayTemplateRequestDto
>;
export type CopyOverlayTemplateResponseDto = z.infer<
  typeof CopyOverlayTemplateResponseDto
>;

// Admin Asset Media Upload DTOs
export const AdminUploadAssetMediaResponseDto = z.object({
  externalId: z.string(),
  type: AssetMediaType,
  status: AssetMediaStatus,
});

// Admin Overlay Template Create/List DTOs
export const AdminCreateOverlayTemplateRequestDto = z.object({
  name: z.string(),
  content: OverlayTemplateContentDto,
});

export const AdminCreateOverlayTemplateResponseDto = z.object({
  id: z.number(),
  externalId: z.string(),
  name: z.string(),
  teamId: z.number(),
});

export const AdminListOverlayTemplatesResponseDto = z.array(
  z.object({
    id: z.number(),
    externalId: z.string(),
    name: z.string(),
    teamId: z.number(),
  }),
);

export type AdminUploadAssetMediaResponseDto = z.infer<
  typeof AdminUploadAssetMediaResponseDto
>;
export type AdminCreateOverlayTemplateRequestDto = z.infer<
  typeof AdminCreateOverlayTemplateRequestDto
>;
export type AdminCreateOverlayTemplateResponseDto = z.infer<
  typeof AdminCreateOverlayTemplateResponseDto
>;
export type AdminListOverlayTemplatesResponseDto = z.infer<
  typeof AdminListOverlayTemplatesResponseDto
>;

export const BackfillOrganisationCreditUsagesRequestDto = z.object({
  organisationId: z.number().int().positive(),
  customPricing: BillingAccountCustomPricingDto,
  isDryRun: z.boolean(),
  limit: z.number().int().positive(),
  cursor: z.number().int().positive().nullish(),
});

export const BackfillOrganisationCreditUsagesResponseDto = z.object({
  organisationId: z.number(),
  billingAccountId: z.number(),
  isDryRun: z.boolean(),
  creditUsageCount: z.number(),
  totalCreditsBefore: z.number(),
  totalCreditsAfter: z.number(),
  nextCursor: z.number().nullable(),
});

export type BackfillOrganisationCreditUsagesRequestDto = z.infer<
  typeof BackfillOrganisationCreditUsagesRequestDto
>;
export type BackfillOrganisationCreditUsagesResponseDto = z.infer<
  typeof BackfillOrganisationCreditUsagesResponseDto
>;

export const BackfillVoiceActorsV4RequestDto = z.object({
  isDryRun: z.boolean(),
  limit: z.number().int().positive().max(50),
  cursor: z.number().int().positive().nullish(),
});

export const BackfillVoiceActorsV4ResponseDto = z.object({
  isDryRun: z.boolean(),
  voiceActors: z.array(
    z.object({
      externalId: z.string(),
      name: z.string(),
      addedLanguages: z.array(z.string()),
      removedLanguages: z.array(z.string()),
      status: z.enum(["UPDATED", "FAILED", "DRY_RUN"]),
      error: z.string().optional(),
    }),
  ),
  nextCursor: z.number().nullable(),
});

export type BackfillVoiceActorsV4RequestDto = z.infer<
  typeof BackfillVoiceActorsV4RequestDto
>;
export type BackfillVoiceActorsV4ResponseDto = z.infer<
  typeof BackfillVoiceActorsV4ResponseDto
>;

// Admin Team Membership DTOs
export const AdminUserTeamDto = z.object({
  teamId: z.number(),
  teamName: z.string().nullable(),
  teamExternalId: z.string(),
  adminIsMember: z.boolean(),
});

export const AdminUserTeamsResponseDto = z.object({
  email: z.string(),
  teams: z.array(AdminUserTeamDto),
});

export const AdminTeamMembershipRequestDto = z.object({
  teamId: z.number(),
});

export const AdminTeamMembershipResponseDto = z.object({
  success: z.boolean(),
});

export type AdminUserTeamDto = z.infer<typeof AdminUserTeamDto>;
export type AdminUserTeamsResponseDto = z.infer<
  typeof AdminUserTeamsResponseDto
>;
export type AdminTeamMembershipRequestDto = z.infer<
  typeof AdminTeamMembershipRequestDto
>;
export type AdminTeamMembershipResponseDto = z.infer<
  typeof AdminTeamMembershipResponseDto
>;

// Admin Invite Management DTOs
export const AdminInviteDto = z.object({
  id: z.number(),
  externalId: z.string(),
  email: z.string(),
  role: z.string(),
  status: z.enum(["pending", "accepted"]),
  createdAt: z.string(),
  acceptedAt: z.string().nullable(),
  teamId: z.number(),
  teamName: z.string().nullable(),
  teamExternalId: z.string(),
  organisationId: z.number().nullable(),
  organisationName: z.string().nullable(),
});

export const AdminInvitesResponseDto = z.object({
  email: z.string(),
  invites: z.array(AdminInviteDto),
});

export const AdminResendInviteRequestDto = z.object({
  inviteId: z.number(),
});

export const AdminResendInviteResponseDto = z.object({
  success: z.boolean(),
});

export type AdminInviteDto = z.infer<typeof AdminInviteDto>;
export type AdminInvitesResponseDto = z.infer<typeof AdminInvitesResponseDto>;
export type AdminResendInviteRequestDto = z.infer<
  typeof AdminResendInviteRequestDto
>;
export type AdminResendInviteResponseDto = z.infer<
  typeof AdminResendInviteResponseDto
>;

// Admin Team Members DTOs
export const AdminTeamMemberDto = z.object({
  id: z.number(),
  userId: z.number(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  role: z.string(),
  createdAt: z.string(),
});

export const AdminTeamWithMembersDto = z.object({
  id: z.number(),
  name: z.string().nullable(),
  externalId: z.string(),
  members: z.array(AdminTeamMemberDto),
});

export const AdminAddTeamMemberRequestDto = z.object({
  email: z.string().email(),
});

export const AdminAddTeamMemberResponseDto = z.object({
  success: z.boolean(),
  membershipId: z.number(),
});

export type AdminTeamMemberDto = z.infer<typeof AdminTeamMemberDto>;
export type AdminTeamWithMembersDto = z.infer<typeof AdminTeamWithMembersDto>;
export type AdminAddTeamMemberRequestDto = z.infer<
  typeof AdminAddTeamMemberRequestDto
>;
export type AdminAddTeamMemberResponseDto = z.infer<
  typeof AdminAddTeamMemberResponseDto
>;
